import { type JSX, useMemo, useState } from 'react';

import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { MultipleControlMapping, MultipleControlMappingProps } from 'Components/MultipleControlMapping/MultipleControlMapping';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_SAVE } from 'Config/Icons';
import { OperationalControl } from 'Models/OperationalControls';

import styles from './ModifyRequirementAssociatedControlsModal.module.css';

export interface ModifyRequirementAssociatedControlsModalProps {
    saveControlSet: (controls: string[]) => Promise<void>;
    currentMappedControlIdentifiers: string[];
    hideModal: () => void;
    controlMappingItems: OperationalControl[];
    controlMappingItemsError?: Error;
    updateRequirementDetails: () => Promise<void>;
}

interface FormState {
    successMessage?: string;
    failureMessage?: string;
}

export const ModifyRequirementAssociatedControlsModal = (props: ModifyRequirementAssociatedControlsModalProps): JSX.Element => {
    const [controlsToAssociate, setControlsToAssociate] = useState<string[]>(props.currentMappedControlIdentifiers);
    const [isModifyingControls, setIsModifyingControls] = useState(false);
    const [formState, setFormState] = useState<FormState>({});
    const modifyControls = async (): Promise<void> => {
        if (controlsToAssociate.length === 0) {
            setFormState({ failureMessage: 'Please select controls.' });
            return;
        }
        try {
            setIsModifyingControls(true);
            await props.saveControlSet(controlsToAssociate);
            await props.updateRequirementDetails();
            setFormState({
                successMessage: 'Controls mapped.',
            });
        } catch (error) {
            setFormState({ failureMessage: error.message });
        } finally {
            setIsModifyingControls(false);
        }
    };

    const multipleControlMappingProps: MultipleControlMappingProps = useMemo(
        () => ({
            controls: props.controlMappingItems,
            handleControlChange: (selectedControls: string[]) => setControlsToAssociate(selectedControls),
            currentMappedControlIdentifiers: props.currentMappedControlIdentifiers,
        }),
        [props.controlMappingItems, props.currentMappedControlIdentifiers]
    );

    if (props.controlMappingItemsError !== undefined) {
        return <Text>{props.controlMappingItemsError.message}</Text>;
    }

    return (
        <Modal open header="Map Requirement to Controls">
            {formState.successMessage && <Alert type="success">{formState?.successMessage}</Alert>}
            {formState.failureMessage && <Alert type="error">{formState?.failureMessage}</Alert>}
            <MultipleControlMapping {...multipleControlMappingProps} />
            <div className={styles.modalFormButtonContainer}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE} disabled={isModifyingControls}>
                    Close
                </Button>
                <Button variant="primary" onClick={modifyControls} fontAwesomeImage={ICON_SAVE} loadingText={'Saving...'} isLoading={isModifyingControls} disabled={formState.successMessage ? true : false || props.controlMappingItems!.length === 0}>
                    Save
                </Button>
            </div>
        </Modal>
    );
};
